import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import { createExpressionBuilder, lambda, DefaultExpressionBuilder } from "../../../../../stream";
import { CsObject, context_object_step, where_rule_context } from "../state/expressions";
import { Pending, Unknown, Result, Failed } from "../Status";
import * as Text from "../state/Text";
import * as Rule from "../state/Rule";
import { iReference } from "../state/Reference";
import * as step from "../state/step";
import { toStream } from "../toStream";
import { registrationDone } from "../state/registerClientState";

export function isResult<T>(value: Result<T | "yes">): value is T {
	switch (value) {
		case Pending:
		case Unknown:
		case Failed:
		case "yes":
			return false;
		default:
			return true;
	}
}

function selectProperty(
	definition: api_app.Ctext__type__property
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Ctext__type__property | "yes">>;
function selectProperty(
	definition: api_app.Cnumber__type__property
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnumber__type__property | "yes">>;
function selectProperty(
	definition: api_app.Cfile
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cfile__type__property | "yes">>;
function selectProperty(
	definition: api_app.Cgroup__type__property
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnode | "yes">>;
function selectProperty(
	definition: api_app.Cstate_group
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cstate_group__type__property | "yes">>;
function selectProperty(
	definition: api_app.Ccollection__type__property
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Ccollection | "yes">>;
function selectProperty(
	definition: step.AppProperty
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<CsObject | "yes">>;
function selectProperty(
	definition: step.AppProperty
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<CsObject | "yes">> {
	const name = definition.parent.parent.key;
	return lambda((node) => {
		if (!isResult(node)) return node;
		let object: any = node.properties.attributes.get(name)?.properties.type.cast("property").properties.type.state.node;
		if (object instanceof api_cs.Cgroup__type__property) {
			object = object.properties.node;
		}
		return <CsObject>object ?? "yes";
	});
}

function propertyStep(root: api_app.Cproperty_step) {
	return selectProperty(root.properties.property.ref.properties.type.state.node);
}

function dependencyStep(
	root: api_app.Cdependency_step
): DefaultExpressionBuilder<api_cs.Cnode, "yes" | typeof Pending | typeof Unknown | typeof Failed> {
	const $type = root.properties.type.state;
	switch ($type.name) {
		case "reference":
			return selectProperty($type.node.properties.property.inferences.text()).pipe((text) => {
				if (isResult(text)) return iReference(text.properties.navigable.cast("yes")).synchronized;
				return text;
			});
		case "reference rule": {
			const name = $type.node.properties.rule.properties.rule.ref.key;
			return selectProperty($type.node.properties.property.inferences.text()).pipe((text) => {
				if (isResult(text))
					return Rule.synchronized(text.properties.navigable.cast("yes").properties.rules.properties.rules.get(name)!);
				return text;
			});
		}
		case "state context rule": {
			const name = $type.node.properties.rule.properties.rule.ref.key;
			return lambda((node: api_cs.Cnode) => {
				if (isResult(node))
					return Rule.synchronized(node.input.position().cast("state").properties.rules.properties.rules.get(name)!);
				return node;
			});
		}
	}
}

function objectPathTail(
	root: api_app.Cobject_path_tail
): DefaultExpressionBuilder<Result<CsObject | "yes">, Result<api_cs.Ccollection | api_cs.Cnode | "yes">> {
	const $has_steps = root.properties.has_steps.state;
	switch ($has_steps.name) {
		case "no":
			return lambda((object: Result<api_cs.Ccollection | api_cs.Cnode | "yes">) => object);
		case "yes": {
			const $type = $has_steps.node.properties.type.state;
			switch ($type.name) {
				case "parent":
					return lambda((node: Result<api_cs.Cnode | "yes">) =>
						!isResult(node) ? node : node.input.position().input.parent().cast("node")
					).pipe(objectPathTail($has_steps.node.properties.tail));
				case "property value":
					return propertyStep($type.node.properties.property).pipe(objectPathTail($has_steps.node.properties.tail));
				case "dependency":
					return dependencyStep($type.node.properties.dependency);
				case "reference set":
					throw new Error("Unsupported 'Reference set' step");
				case "state":
					return lambda((stategroup: Result<api_cs.Cstate_group__type__property | "yes">) =>
						!isResult(stategroup)
							? stategroup
							: toStream(stategroup.component_root.properties.location).stateSwitch({
									local: () => Failed as Result<api_cs.Ccollection | api_cs.Cnode | "yes">,
									remote: () =>
										toStream(stategroup.properties.is_modified).stateSwitch({
											no: () => {
												const state = stategroup.properties.states.get($type.node.properties.state.ref.key_value)!;
												return toStream(state.properties.is_selected).stateSwitch({
													no: () => "yes" as Result<api_cs.Ccollection | api_cs.Cnode | "yes">,
													yes: () => state.properties.node,
												})();
											},
											yes: () => Failed as Result<api_cs.Ccollection | api_cs.Cnode | "yes">,
										})(),
							  })()
					).pipe(objectPathTail($has_steps.node.properties.tail));
			}
		}
	}
}

export function entryReferenceSynchronized(
	constraining_text: api_cs.Ctext__type__property,
	definition: api_app.Centry_reference_definition
) {
	return registrationDone(constraining_text, (subscribed) => {
		if (subscribed === false) return Failed;
		return createExpressionBuilder(Text.valueModified(constraining_text)).pipe((modified) => {
			if (modified === true) return Failed;
			return createExpressionBuilder(constraining_text.component_root)
				.pipe(context_object_step(definition.properties.collection_path.properties.head))
				.pipe(objectPathTail(definition.properties.collection_path.properties.path))
				.pipe((res) => (isResult(res) ? "yes" : res))();
		})();
	});
}

export function whereRuleObjectPath(
	definition: api_app.Cwhere_rule_object_path
): DefaultExpressionBuilder<api_cs.Crules, Result<"yes">> {
	const $context = definition.properties.context.state;
	switch ($context.name) {
		case "this": {
			const $step = $context.node.properties.head;
			switch ($step.properties.context.state.name) {
				case "this":
					return where_rule_context()
						.pipe(objectPathTail(definition.properties.path))
						.pipe(lambda((res) => (isResult(res) ? "yes" : res)));
				case "variable":
					return lambda(
						(rule: api_cs.Crules) => iReference(rule.parent.input.definer().cast("reference")).synchronized
					);
				case "dynamic user":
					return lambda(() => "yes");
			}
			break;
		}
		case "sibling rule":
			return lambda((rule: api_cs.Crules) => {
				const sibling = rule.parent.properties.rules.get($context.node.properties.rule.entry)!;
				return Rule.synchronized(sibling);
			});
	}
}
